// File: src/pages/Trader/OrdersAndWithdrawals/Settlements/components/CreateSettlementsWithdrawal/CreateSettlementsWithdrawal.tsx
import React from 'react';
import { createPortal } from 'react-dom';
import { useEscapeKey } from '../../../../../../utils/hooks/useEscapeKey';
import { useForm, FormProvider, SubmitHandler, Controller } from 'react-hook-form';
import SelectType, { TItem } from '../CustomFilterModal/SelectType';
import { ControlledInput } from '../../../../../../components/ControlledInput/ControlledInput';
import { Loading } from '../../../../../../components/Loading/Loading';
import { getRequestErrorMessage, pushNotify } from '../../../../../../components/Notifications';
import { sanitizeCurrencyAmount } from '../../../../../../utils/functions/currencyAmount';
import { useCreateSettlementMutation } from '../../../../../../redux/services/SettlementsApi';
import { useGetSelfQuery } from '../../../../../../redux/services/MerchantApi';
import styles from './CreateSettlementsWithdrawal.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormValues {
  currency: string;
  amount: string;
  address: string;
}

export const CreateSettlementsWithdrawal: React.FC<Props> = ({ open, onClose, onSuccess }) => {
  const { data: self, isLoading: selfLoading } =
    useGetSelfQuery(undefined, { skip: !open, refetchOnMountOrArgChange: true });
  const [createSettlement, { isLoading: isCreating }] = useCreateSettlementMutation();
  const maxWithdraw = self?.balance_withdrawal_usdt_check ?? 0;

  const methods = useForm<FormValues>({
    defaultValues: { currency: 'USDT-TRC20', amount: '', address: '' },
    mode: 'onChange'
  });
  const {
    handleSubmit,
    formState: { errors, isValid },
    control
  } = methods;

  const onSubmit: SubmitHandler<FormValues> = async ({ currency, amount, address }) => {
    const num = Number(sanitizeCurrencyAmount(amount) || '');
    try {
      await createSettlement({
        direction: 'PLATFORM_TO_MERCHANT',
        platform_wallet_address: address,
        currency,
        amount: num
      }).unwrap();
      pushNotify('Заявка на вывод создана', 'Готово');
      onSuccess?.();
      onClose();
    } catch (error) {
      pushNotify(getRequestErrorMessage(error, 'Не удалось создать заявку на вывод'), 'Ошибка');
    }
  };

  useEscapeKey(open && !isCreating, onClose);
  if (!open) return null;
  if (selfLoading || isCreating) {
    return createPortal(<Loading />, document.body);
  }

  const currencyItems: TItem<'USDT-TRC20'>[] = [
    { id: 'USDT-TRC20', name: 'USDT-TRC20' }
  ];

  return createPortal(
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="create-settlement-withdrawal-title" onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 id="create-settlement-withdrawal-title" className={styles.title}>Новый вывод средств</h2>
          <button type="button" aria-label="Закрыть" className={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>
        <p className={styles.subtitle}>Введите данные для создания вывода средств.</p>

        <FormProvider {...methods}>
          <form id="withdraw-form" className={styles.form} onSubmit={handleSubmit(onSubmit)}>
            {/* Валюта */}
            <div className={styles.field}>
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <SelectType
                    title="Валюта"
                    items={currencyItems}
                    value={field.value}
                    onChange={field.onChange}
                    error={Boolean(errors.currency)}
                  />
                )}
              />
            </div>

            {/* Сумма */}
            <div className={styles.field}>
              <label className={styles.label}>Сумма</label>
              <ControlledInput
                name="amount"
                placeholder="Введите сумму"
                rules={{
                  required: 'Обязательно',
                  validate: v => {
                    const val = Number(sanitizeCurrencyAmount(v) || '');
                    if (!Number.isFinite(val) || val <= 0) return 'Должна быть больше 0';
                    if (val > maxWithdraw) return `Максимальная сумма не может превышать ${maxWithdraw.toFixed(2)}`;
                    return true;
                  }
                }}
                errorText={errors.amount?.message as string}
                type="text"
                blockStyle={{ width: '100%' }}
              />
              {errors.amount && (
                <span className={styles.errorText}>{errors.amount.message}</span>
              )}
            </div>

            {/* Адрес */}
            <div className={styles.field}>
              <label className={styles.label}>Адрес для вывода</label>
              <ControlledInput
                name="address"
                placeholder="Введите адрес"
                rules={{ required: 'Обязательно' }}
                errorText={errors.address?.message as string}
                type="text"
                blockStyle={{ width: '100%' }}
              />
            </div>
          </form>
        </FormProvider>

        {/* Кнопка “Создать” справа-внизу */}
        <div className={styles.actions}>
          <button
            type="submit"
            form="withdraw-form"
            className={styles.button}
            disabled={!isValid || isCreating}
          >
            Создать
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};
